<template>
  <b-dropdown
    :id="props.id"
    v-model="isDropdownOpen"
    :class="[
      'custom-dropdown',
      props.className,
      props.validation.$error ? 'invalid' : '',
      disabled && 'tw-pointer-events-none tw-opacity-50',
    ]"
    :data-cy="dataCyId"
    @keydown.down="moveSelectionDown(1)"
    @show="showDropdow"
    @keydown="selectItem"
    @keydown.up="moveSelectionUp(-1)"
  >
    <slot />
    <template #button-content>
      <slot name="button-content">
        <div
          v-if="props.type === 'single'"
          class="tw-flex tw-justify-between tw-items-center"
        >
          <div class="tw-flex tw-items-center">
            <img
              v-if="props.data[0] && props.data[0]?.iconSrc"
              class="tw-mr-[5px]"
              :src="props.data[0]?.iconSrc || ''"
            />

            <span
              v-if="props.data[0]"
              :class="`tw-text-[#231F20] ${classTextName}`"
              >{{ props.data[0]?.name }}</span
            >
            <span
              v-else
              :class="`placeholder-container ${classTextPlaceholder}`"
              >{{ placeholder }}</span
            >
          </div>
          <div class="tw-flex tw-items-center">
            <ArrowDownTableIcon
              :class="'right-arrow-icon'"
              :color="arrowColor"
            />
          </div>
        </div>
        <div
          v-if="props.type === 'multi-select'"
          class="role-container tw-flex tw-justify-between tw-items-center"
        >
          <div class="role-inner tw-flex tw-gap-[5px]">
            <span
              v-if="props.data.length === 0"
              :class="`placeholder-container ${classTextPlaceholder}`"
              >{{ placeholder }}</span
            >
            <div
              v-for="item in props.data"
              :key="item.id"
              class="role tw-text-sm tw-font-medium tw-flex tw-items-center tw-px-[13px] tw-text-[#231F20]"
            >
              <span>{{ item.name }}</span>
            </div>
          </div>
          <ArrowDownTableIcon :class="'right-arrow-icon'" :color="arrowColor" />
        </div>
      </slot>
    </template>
  </b-dropdown>

  <b-form-invalid-feedback :class="{ 'is-invalid': validation.$error }">
    <VRuntimeTemplate :template="validation?.$errors?.[0]?.$message" />
  </b-form-invalid-feedback>
</template>
<script setup lang="ts">
import VRuntimeTemplate from "vue3-runtime-template";
import { IDropDownData } from "@/interfaces/common/IDropDown";
import { PropType, ref, watch } from "vue";
import store from "@/store";
import { OVERLAY_KEYBOARD } from "@/utils/constants";
import ArrowDownTableIcon from "@/components/svgs/arrowDownTableIcon.vue";
const isDropdownOpen = ref(false);
type ITypeProps = "single" | "multi-select" | "role-item";

const props = defineProps({
  id: { type: String, required: true },
  className: { type: String, required: false, default: "" },
  classTextName: { type: String, required: false, default: "" },
  classTextPlaceholder: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  validation: { type: Object, default: () => ({}) },
  showInfo: { type: Boolean, default: false },
  data: {
    type: Array as PropType<IDropDownData[]>,
    required: true,
    default: () => [],
  },
  type: { type: String as PropType<ITypeProps>, default: "single" },
  disabled: { type: Boolean, default: false },
  arrowColor: { type: String, default: "#387DFF" },
  dataCyId: { type: String, default: "" },
});
const emit = defineEmits([
  "update:moveSelectionDown",
  "update:showDrop",
  "update:enterValue",
  "update:moveSelectionUp",
]);
const moveSelectionDown = (step) => {
  emit("update:moveSelectionDown", step);
};
const moveSelectionUp = (step) => {
  emit("update:moveSelectionUp", step);
};

const showDropdow = () => {
  emit("update:showDrop");
  store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.DROPDOWN);
};

const selectItem = (event) => {
  if (event.key == "ArrowDown") {
    isDropdownOpen.value = true;
    store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.DROPDOWN);
  }
  if (event.key == "Enter") {
    emit("update:enterValue");
    isDropdownOpen.value = false;
    setTimeout(() => {
      store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
    }, 1000);
  }
};

watch(isDropdownOpen, (newValue, oldValue) => {
  if (!newValue && oldValue) {
    setTimeout(() => {
      store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
    }, 1000);
  }
});
</script>
<style lang="scss">
.placeholder-container {
  line-height: normal;
  border-left: 1px solid;
  padding-left: 6px;
}

.dropdown-toggle.show .right-arrow-icon {
  transform: rotate(-180deg);
}

.dropdown-toggle .right-arrow-icon {
  transition: transform 0.3s ease;
}

.normal-dropdown-container {
  width: 100%;

  .btn-secondary {
    width: 100%;
    text-align: left;
    line-height: 100%;
    padding: 19px 25px;
    color: #211f58;
    font-size: 15px;
    font-weight: 500;
    background-color: #fff;
    border: 1px solid #eff0f1;
    box-shadow: none;
  }

  .dropdown-toggle::after {
    display: none;
  }
}

.gray-dropdown-container {
  width: 100%;

  .btn-secondary {
    width: 100%;
    text-align: left;
    line-height: 100%;
    padding: 11px 20px;

    color: #b7b7b7;
    font-size: 13px;

    background-color: #eff0f180;
    border: 1px solid #eff0f1 !important;
    box-shadow: none;
    border-radius: 6px;
    &:hover,
    &:focus,
    &:active,
    &:active:focus {
      color: #b7b7b7;
      font-size: 13px;

      background-color: #eff0f180;
      border: 1px solid #eff0f1;
      box-shadow: none !important;
    }
  }

  .dropdown-toggle::after {
    display: none;
  }

  &.tw-text-normal {
    .btn {
      color: #231f20;
    }
  }
}

.role-container {
  align-items: center;

  .role {
    color: #231f20;
    height: 34px;
    border: 1px solid #eff0f1;
    border-radius: 100px;
    font-size: 11px;
    line-height: 14.3px;
    background-color: #ffffff;
  }
}

.custom-dropdown {
  &.invalid {
    .btn-secondary {
      background-color: #fee5e2;
      border-color: #f7553c;
    }
  }

  .invalid-feedback {
    font-size: 11px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 500;
    color: #f7553c;
    line-height: 130%;
    margin-top: 10px;
  }

  .is-invalid {
    display: block;
    border: 1px solid #f7553c;
    background: #fee5e2;

    &::after {
      content: "";
    }
  }
}

@media (max-width: 420px) {
  .role-inner {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
}

.dropdown-menu {
  max-height: 230px !important;
  overflow: auto !important;
}

.gray-dropdown-container.white-background {
  .btn-secondary {
    background-color: #ffffff;
  }
}
.gray-dropdown-container.blue-background {
  .btn-secondary {
    background-color: #f7f7f8;
  }
}
.gray-dropdown-container-advance.white-background {
  .btn-secondary {
    padding: 12px;
    background: rgba(239, 241, 241, 0.5);
  }
}
</style>
